import { applyDamage, hitTarget, playerDamageMult, playerDamagePct, rollDamage, scaleDamage } from '../combat';
import { recomputeStats } from '../equipment';
import type { Hazard, Monster, Player } from '../types';
import { entitiesOfKind, getPlayer, type World } from '../world';

/** Tolérance sur les comparaisons de dates (le temps du monde s'accumule en flottants). */
const EPS = 1e-9;

/** Poison : un tic toutes les `interval` secondes, y compris à l'échéance (durée / intervalle tics au total). Fin à `until` ou à la mort. */
function tickPoison(world: World, p: Player): void {
  for (const m of entitiesOfKind<Monster>(world, 'monster')) {
    const po = m.poison;
    if (!po) continue;
    if (!m.alive) { m.poison = null; continue; }
    while (m.alive && po.nextTick <= world.time + EPS && po.nextTick <= po.until + EPS) {
      const amount = rollDamage(world.rng, scaleDamage(po.damage, playerDamagePct(world, p)), p.stats, m.resistances, playerDamageMult(world, m));
      applyDamage(world, m, amount, po.damage.element, m.pos);
      po.nextTick += po.interval;
    }
    if (!m.alive || world.time >= po.until) m.poison = null;
  }
}

/** Ronces : disparaissent à `until` ; sinon un tic (dégâts, ralentissement, poison) sur chaque monstre vivant dans le rayon. */
function tickHazards(world: World, p: Player): void {
  for (const h of entitiesOfKind<Hazard>(world, 'hazard')) {
    if (world.time >= h.until) { world.entities.delete(h.id); continue; }
    while (h.nextTick <= world.time + EPS) {
      for (const mon of entitiesOfKind<Monster>(world, 'monster')) {
        if (!mon.alive || Math.hypot(mon.pos.x - h.pos.x, mon.pos.y - h.pos.y) > h.radius + mon.radius) continue;
        hitTarget(world, p, mon, scaleDamage(h.tick.damage, playerDamagePct(world, p)), { lifestealPct: 0, slow: h.slow, poison: h.poison }, mon.pos);
      }
      h.nextTick += h.tick.interval;
    }
  }
}

/** États temporaires : buffs du joueur expirés, tics de poison des monstres, ronces posées. */
export function statusSystem(world: World): void {
  const p = getPlayer(world);
  if (!p) return;
  const kept = p.buffs.filter((b) => b.until > world.time);
  if (kept.length !== p.buffs.length) {
    p.buffs = kept;
    recomputeStats(world, p);
  }
  tickPoison(world, p);
  tickHazards(world, p);
}
